import { api } from './_client';
import type {
  MetadataEntry,
  StatsResponse,
  DroneStatsResponse,
  HealthResponse,
  CreateDroneRequest,
  CreateDroneResponse,
  EngineStartupResponse,
  DroneStatus,
  DroneStatusResponse,
  LegacyHours,
  OwnershipType,
} from '../../types/api';

export const dronesApi = {
  // Health check
  async healthCheck(): Promise<HealthResponse> {
    const response = await api.get<HealthResponse>('/health');
    return response.data;
  },

  // Get all serial numbers
  async getSerialNumbers(flightTypeFilter?: string): Promise<string[]> {
    const params = new URLSearchParams();
    if (flightTypeFilter) params.append('flight_type_filter', flightTypeFilter);

    const url = `/serial-numbers${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<string[]>(url);
    return response.data;
  },

  // Get metadata for a specific serial number
  async getMetadata(
    serialNumber: string,
    startDate?: string,
    endDate?: string,
    flightTypeFilter?: string
  ): Promise<MetadataEntry[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (flightTypeFilter) params.append('flight_type_filter', flightTypeFilter);

    const url = `/metadata/${serialNumber}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<MetadataEntry[]>(url);
    return response.data;
  },

  // Get health metadata for a specific serial number (flight_hours > 0 only)
  async getHealthMetadata(
    serialNumber: string,
    flightTypeFilter?: string
  ): Promise<MetadataEntry[]> {
    const params = new URLSearchParams();
    if (flightTypeFilter) params.append('flight_type_filter', flightTypeFilter);

    const url = `/health-metadata/${serialNumber}${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    const response = await api.get<MetadataEntry[]>(url);
    return response.data;
  },

  // Get statistics
  async getStats(
    serialNumber?: string,
    _unused?: string,
    flightTypeFilter?: string
  ): Promise<StatsResponse> {
    const params = new URLSearchParams();
    if (serialNumber) params.append('serial_number', serialNumber);
    if (flightTypeFilter) params.append('flight_type_filter', flightTypeFilter);

    const url = `/stats${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<StatsResponse>(url);
    return response.data;
  },

  // Get drone-specific statistics including hub current metrics
  async getDroneStats(
    serialNumber: string,
    startDate?: string,
    endDate?: string,
    flightTypeFilter?: string
  ): Promise<DroneStatsResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (flightTypeFilter) params.append('flight_type_filter', flightTypeFilter);

    const url = `/drone-stats/${serialNumber}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get<DroneStatsResponse>(url);
    return response.data;
  },

  // Get status of a specific drone
  async getDroneStatus(serialNumber: string): Promise<{ status: string }> {
    const response = await api.get<{ status: string }>(`/drone/${serialNumber}/status`);
    return response.data;
  },

  // Update status of a specific drone
  async updateDroneStatus(serialNumber: string, status: DroneStatus): Promise<DroneStatusResponse> {
    const response = await api.put<DroneStatusResponse>(`/drone/${serialNumber}/status`, {
      status,
    });
    return response.data;
  },

  // Update metadata of a specific drone
  async updateDroneMetadata(
    serialNumber: string,
    metadata: Record<string, unknown>
  ): Promise<{ success: boolean; message: string; metadata: Record<string, unknown> }> {
    const response = await api.patch<{
      success: boolean;
      message: string;
      metadata: Record<string, unknown>;
    }>(`/drone/${serialNumber}/metadata`, { metadata });
    return response.data;
  },

  // Upload drone picture
  async uploadDronePicture(
    droneSerial: string,
    file: File
  ): Promise<{ success: boolean; message: string; picture_url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{ success: boolean; message: string; picture_url: string }>(
      `/picture/${droneSerial}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Get drone picture presigned URL
  async getDronePictureUrl(droneSerial: string): Promise<{
    success: boolean;
    message?: string;
    picture_url: string | null;
    expires_in?: number;
  }> {
    const response = await api.get<{
      success: boolean;
      message?: string;
      picture_url: string | null;
      expires_in?: number;
    }>(`/picture-url/${droneSerial}`);
    return response.data;
  },

  // Create a new drone
  async createDrone(droneData: CreateDroneRequest): Promise<CreateDroneResponse> {
    const response = await api.post<CreateDroneResponse>('/create', droneData);
    return response.data;
  },

  // Get all drones with pre-calculated hours, status, and maintenance hours
  async getAllDronesSimple(): Promise<
    Array<{
      serial_number: string;
      model?: string | null;
      total_engine_hours: number | null;
      total_flight_hours: number | null;
      status: DroneStatus;
      next_maintenance_hours: number | null;
      overdue_hours: number | null;
      picture_url?: string | null;
      metadata?: Record<string, unknown>;
      legacy_hours?: LegacyHours;
      organization_name?: string | null;
      ownership_type?: OwnershipType | null;
      lease_end_date?: string | null;
    }>
  > {
    const response = await api.get('/all');
    return response.data;
  },

  // Get all drones with organization details (XER admin only)
  async getAllDronesWithOrganizations(): Promise<
    Array<{
      serial_number: string;
      organization_id: string | null;
      organization_name: string | null;
      organization_slug: string | null;
      status: DroneStatus;
      flight_hours: number | null;
      engine_hours: number | null;
    }>
  > {
    const response = await api.get('/all-with-organizations');
    return response.data;
  },

  // Update drone's organization assignment (XER admin only)
  async updateDroneOrganization(
    serialNumber: string,
    organizationId: string
  ): Promise<{
    success: boolean;
    message: string;
    drone?: {
      serial_number: string;
      organization_id: string | null;
    };
  }> {
    const response = await api.patch(`/${serialNumber}/organization`, {
      organization_id: organizationId,
    });
    return response.data;
  },

  // Update drone's ownership information (XER admin only)
  async updateDroneOwnership(
    serialNumber: string,
    ownershipType: OwnershipType | null,
    leaseEndDate: string | null
  ): Promise<{
    success: boolean;
    message: string;
    serial_number: string;
    ownership_type: OwnershipType | null;
    lease_end_date: string | null;
  }> {
    const response = await api.patch(`/${serialNumber}/ownership`, {
      ownership_type: ownershipType,
      lease_end_date: leaseEndDate,
    });
    return response.data;
  },

  // Get health trend analysis for a drone
  async getHealthTrendAnalysis(
    serialNumber: string,
    section: string
  ): Promise<{
    serial_number: string;
    section: string;
    analyses: Array<{
      parameter: string;
      trend_value: number | null;
      confidence: number;
      forecast_value: number | null;
      forecast_date: string | null;
      data_points: number;
      mean_value?: number;
      chart_data?: Array<{
        date: string;
        value: number;
        stdDev?: number | null;
      }>;
      latest_value?: number | null;
      latest_status?: 'normal' | 'warning' | 'unknown';
      latest_date?: string | null;
      unit?: string;
      // From packages/fleet_tools/health_catalogue.py (#1207).
      title?: string;
      range?: { min: number; max: number; unit: string; label?: string } | null;
      bad_direction?: 'up' | 'down' | 'either' | 'none';
      on_panel?: boolean;
    }>;
  }> {
    const params = new URLSearchParams();
    params.append('section', section);

    const url = `/health-trend/${serialNumber}?${params.toString()}`;
    const response = await api.get(url);
    return response.data;
  },

  // Get engine startup analysis events for a drone
  async getEngineStartupEvents(serialNumber: string): Promise<EngineStartupResponse> {
    const response = await api.get<EngineStartupResponse>(`/engine-startup/${serialNumber}`);
    return response.data;
  },
};
